/**
 * @typedef {import('../../../Auth/AuthService').default} AuthService
 * @typedef {import('./AddsAgentOffer').default} AddsAgentOffer
 * @typedef {import('./AddsOffer').default} AddsOffer
 * @typedef {import('./AddsOfferImage').default} AddsOfferImage
 * @typedef {import('./AddsGroupOffer').default} AddsGroupOffer
 * @typedef {import('./Dependencies').default} Dependencies
 * @typedef {import('./EditsOffer').default} EditsOffer
 * @typedef {import('./GetsAgentOffers').default} GetsAgentOffers
 * @typedef {import('./GetsDomainOffers').default} GetsDomainOffers
 * @typedef {import('./GetsGroupOffers').default} GetsGroupOffers
 * @typedef {import('./Offer').default} Offer
 * @typedef {import('./OfferFields').default} OfferFields
 * @typedef {import('./OfferResponse').default} OfferResponse
 * @typedef {import('./PaginatedOfferResponse').default} PaginatedOfferResponse
 * @typedef {import('./UpdatesNetworkVendors').default} UpdatesNetworkVendors
 * @typedef {{
 * 	agent?: { _id?: string | null } | null;
 * 	administrator?: { _id?: string | null } | null;
 * }} MeInfoMeta
 */

import { translate } from '../../Internationalization';
import apiUrls from '../../../config/local/api-urls';
import ServiceError from '../../../shared/Errors/ServiceError';

/**
 * @implements {AddsAgentOffer}
 * @implements {AddsOffer}
 * @implements {AddsOfferImage}
 * @implements {AddsGroupOffer}
 * @implements {EditsOffer}
 * @implements {GetsAgentOffers}
 * @implements {GetsDomainOffers}
 * @implements {GetsGroupOffers}
 * @implements {UpdatesNetworkVendors}
 */
export default class OfferService {
	/** @type {Dependencies['authService']} */
	authService;

	/** @type {Dependencies['httpClient']} */
	httpClient;

	/**
	 * @type {{
	 * 	inFlight: Promise<MeInfoMeta | null> | null;
	 * 	meta: MeInfoMeta | null;
	 * 	token: string | null;
	 * }}
	 */
	_meInfoCache;

	/**
	 * @param {any} vendor
	 * @returns {{ category: string, email: string | null, id: string, name: string, phoneNumber: string | null, websiteUrl: string | null } | null}
	 */
	static mapPreferredVendor(vendor) {
		if (!vendor) {
			return null;
		}

		return {
			category: vendor.category,
			email: vendor.email ?? null,
			id: vendor.id,
			name: vendor.name,
			phoneNumber: vendor.phoneNumber ?? vendor.phone_number ?? null,
			websiteUrl: vendor.websiteUrl ?? vendor.website_url ?? null,
		};
	}

	/**
	 * @param {Dependencies} dependencies
	 */
	constructor(dependencies) {
		this.authService = dependencies.authService;
		this.httpClient = dependencies.httpClient;
		this._meInfoCache = {
			inFlight: null,
			meta: null,
			token: null,
		};
	}

	/**
	 * Caches /me/info per access token to avoid repeated user-info calls
	 * during the same session.
	 * @returns {Promise<MeInfoMeta | null>}
	 */
	async getMeInfoMeta() {
		const token = this.authService.getAccessToken();
		const current = this._meInfoCache;

		if (current.token === token && current.meta) {
			return current.meta;
		}

		if (current.token === token && current.inFlight) {
			return current.inFlight;
		}

		const inFlight = this.httpClient
			.get(
				apiUrls.me.info,
				new Headers({
					Authorization: `Bearer ${token}`,
				})
			)
			.then((response) => {
				if (!response.isOk) {
					throw new ServiceError(
						'Unable to get user details.',
						response?.body
					);
				}

				const meta = response?.body?.results?.meta ?? null;

				this._meInfoCache = {
					inFlight: null,
					meta,
					token,
				};

				return meta;
			})
			.catch((error) => {
				if (
					this._meInfoCache.token === token &&
					this._meInfoCache.inFlight === inFlight
				) {
					this._meInfoCache.inFlight = null;
				}

				throw error;
			});

		this._meInfoCache = {
			inFlight,
			meta: null,
			token,
		};

		return inFlight;
	}

	/**
	 * @returns {Promise<string>}
	 */
	async getAgentId() {
		const meta = await this.getMeInfoMeta();
		const agentId = meta?.agent?._id;

		if (!agentId) {
			throw new ServiceError('Unable to get agent details.', null);
		}

		return agentId;
	}

	/**
	 * @returns {Promise<string>}
	 */
	async getAdministratorId() {
		const meta = await this.getMeInfoMeta();
		const administratorId = meta?.administrator?._id;

		if (!administratorId) {
			throw new ServiceError(
				'Unable to get administrator details.',
				null
			);
		}

		return administratorId;
	}

	/**
	 * @param {number} page
	 * @returns {Promise<PaginatedOfferResponse>}
	 */
	async getAgentOffers(page = 1) {
		const params = new URLSearchParams({
			page: page.toString(),
		});

		// prettier-ignore
		const url = `${apiUrls.me.offers.view}/${await this.getAgentId()}/offers/?${params.toString()}`;

		const response = await this.httpClient.get(
			url,
			new Headers({
				Authorization: `Bearer ${this.authService.getAccessToken()}`,
			})
		);

		if (!response.isOk) {
			throw new ServiceError('Unable to get offers.', response.body);
		}

		const totalCount = response.body.total;

		const offers = response.body.offers.map(
			/**
			 * @param {OfferResponse} offer
			 */
			(offer) => ({
				id: offer.id,
				offerCategory: offer.category,
				offerDescription: offer.description,
				offerName: offer.title,
				offerZip: offer.postal_code,
				status: offer.is_active,
				total: totalCount,
				vendorName: offer.vendor.name,
			})
		);

		const categoryList = response.body.category_list;
		const pages = response.body.pages;
		const total = response.body.total;

		return { categoryList, offers, pages, total };
	}

	/**
	 * @param {string} category
	 * @param {number} page
	 * @returns {Promise<PaginatedOfferResponse>}
	 */
	async getAgentOffersByCategory(category, page = 1) {
		const params = new URLSearchParams({
			page: Number(page).toString(),
		});

		// prettier-ignore
		const url = `${apiUrls.me.offers.view}/${await this.getAgentId()}/offers/categories/${category}/?${params.toString()}`;

		const response = await this.httpClient.get(
			url,
			new Headers({
				Authorization: `Bearer ${this.authService.getAccessToken()}`,
			})
		);

		if (!response.isOk) {
			throw new ServiceError('Unable to get offers.', response.body);
		}

		const offers = response.body.offers.map(
			/**
			 * @param {OfferResponse} offer
			 */
			(offer) => {
				const isActive = offer.is_active ?? false;
				return {
					id: offer.id,
					offerCategory: offer.category,
					offerDescription: offer.description,
					offerName: offer.title,
					offerZip: offer.postal_code,
					status: Boolean(isActive),
					vendorName: offer.vendor.name,
				};
			}
		);

		return {
			offers,
			pages: response.body.pages,
			total: response.body.total,
		};
	}

	/**
	 * @param {number} page
	 * @param {{relatedType?: 'agent' | 'office' | 'tenant' | 'local'}} [options]
	 * @returns {Promise<PaginatedOfferResponse>}
	 */
	async getDomainOffers(page = 1, options = {}) {
		const { relatedType } = options;
		const params = new URLSearchParams({
			page: Number(page).toString(),
		});
		if (relatedType) {
			params.append('related_type', relatedType);
		}

		const response = await this.httpClient.get(
			`${
				apiUrls.me.offers.viewDomain
			}/${await this.getAdministratorId()}/offers/?${params.toString()}`,
			new Headers({
				Authorization: `Bearer ${this.authService.getAccessToken()}`,
			})
		);

		if (!response.isOk) {
			throw new ServiceError('Unable to get offers.', response.body);
		}

		const totalVendors = response.body.total;
		const offers = response.body.offers.map(
			/**
			 * @param {OfferResponse} offer
			 */
			(offer) => {
				const isActive = offer.is_active ?? false;

				return {
					id: offer.id,
					offerCategory: offer.category,
					offerDescription: offer.description,
					offerName: offer.title,
					offerZip: offer.postal_code,
					status: Boolean(isActive),
					total: totalVendors,
					vendorName: offer.vendor.name,
				};
			}
		);

		const pages = response.body.pages;
		const total = response.body.total;

		return { offers, pages, total };
	}

	/**
	 * @returns {Promise<PaginatedOfferResponse>}
	 */
	async getGroupOffers(page = 1) {
		const params = new URLSearchParams({
			page: Number(page).toString(),
		});

		const response = await this.httpClient.get(
			`${
				apiUrls.me.offers.viewDomain
			}/${await this.getAdministratorId()}/offers/groups/?${params.toString()}`,
			new Headers({
				Authorization: `Bearer ${this.authService.getAccessToken()}`,
			})
		);

		if (!response.isOk) {
			throw new ServiceError('Unable to get offers.', response.body);
		}

		const offers = response.body.offers.map(
			/**
			 * @param {OfferResponse} offer
			 */
			(offer) => {
				const isActive = offer.is_active ?? false;

				return {
					id: offer.id,
					offerCategory: offer.category,
					offerDescription: offer.description,
					offerName: offer.title,
					offerZip: offer.postal_code,
					status: Boolean(isActive),
					vendorName: offer.vendor.name,
				};
			}
		);

		const pages = response.body.pages;
		const total = response.body.total;

		return { offers, pages, total };
	}

	/** @returns {Promise<number>} */
	async getTotalOffers() {
		const response = await this.httpClient.get(
			`${apiUrls.me.offers.view}/${await this.getAgentId()}/offers`,
			new Headers({
				Authorization: `Bearer ${this.authService.getAccessToken()}`,
			})
		);

		if (!response.isOk) {
			throw new ServiceError('Unable to get offers.', response.body);
		}

		return response.body.total;
	}

	/**
	 * @param {Offer} data
	 * @returns {Promise<string>}
	 */
	async addOffer(data) {
		const isActive = data.isActive === 'active';
		const response = await this.httpClient.post(
			apiUrls.me.offers.add,
			new Headers({
				Authorization: `Bearer ${this.authService.getAccessToken()}`,
				'Content-Type': 'application/json',
			}),
			{
				/* eslint-disable camelcase -- Mapping request body */

				category: data.category,
				description: data.description,
				end_date: data.endDate,
				is_active: isActive,
				owner: data.owner ?? undefined,
				postal_code: data.postalCode,
				start_date: data.startDate,
				title: data.title ?? null,
				vendor: {
					email_address: data.vendorEmail,
					name: data.vendorName,
					phone_number: data.vendorPhone,
				},
				website_url: data.website,
				/* eslint-enable camelcase */
			}
		);

		if (!response.isOk) {
			throw new ServiceError('Unable to add offer.', response.body);
		}

		return response.body.id;
	}

	/**
	 * @param {string} propertyId
	 * @param {{ category: string, vendorEmail: string, vendorName: string, vendorPhone: string, website: string }} data
	 * @param {{ confirmExisting?: boolean }} [options]
	 * @returns {Promise<{ id: string, requiresConfirmation: boolean, vendor: { category: string, email: string | null, id: string, name: string, phoneNumber: string | null, websiteUrl: string | null } | null }>}
	 */
	async addPreferredVendor(propertyId, data, options = {}) {
		const response = await this.httpClient.post(
			apiUrls.preferredVendors.add.replace(':propertyId', propertyId),
			new Headers({
				Authorization: `Bearer ${this.authService.getAccessToken()}`,
				'Content-Type': 'application/json',
			}),
			{
				/* eslint-disable camelcase -- Mapping request body */
				category: data.category,
				confirm_existing: options.confirmExisting ?? false,
				email: data.vendorEmail,
				name: data.vendorName,
				phone: data.vendorPhone,
				website_url: data.website,
				/* eslint-enable camelcase */
			}
		);

		if (!response.isOk) {
			throw new ServiceError(
				'Unable to add preferred vendor.',
				response.body
			);
		}

		return {
			id: response.body.id,
			requiresConfirmation: response.body.requires_confirmation === true,
			vendor: OfferService.mapPreferredVendor(response.body.vendor),
		};
	}

	/**
	 * @param {string} propertyId
	 * @param {{ category?: string }} [options]
	 * @returns {Promise<{ category: string, id: string, name: string, phoneNumber: string | null, websiteUrl: string | null }[]>}
	 */
	async getPreferredVendors(propertyId, options = {}) {
		const params = new URLSearchParams();

		if (options.category) {
			params.set('category', options.category);
		}

		const url = params.toString()
			? `${apiUrls.preferredVendors.view.replace(
					':propertyId',
					propertyId
			  )}?${params.toString()}`
			: apiUrls.preferredVendors.view.replace(':propertyId', propertyId);
		const response = await this.httpClient.get(
			url,
			new Headers({
				Authorization: `Bearer ${this.authService.getAccessToken()}`,
			})
		);

		if (!response.isOk) {
			throw new ServiceError(
				'Unable to get preferred vendors.',
				response.body
			);
		}

		return (response.body.vendors ?? []).map((vendor) =>
			OfferService.mapPreferredVendor(vendor)
		);
	}

	/**
	 * @param {string} offerId
	 * @returns {Promise<Offer>}
	 */
	async getSingleOffer(offerId) {
		const response = await this.httpClient.get(
			`${apiUrls.offers.root}/${offerId}`,
			new Headers({
				Authorization: `Bearer ${this.authService.getAccessToken()}`,
			})
		);

		if (!response.isOk) {
			throw new ServiceError('Unable to get offer.', response.body);
		}

		return {
			category: response.body.category,
			description: response.body.description,
			endDate: response.body.end_date,
			id: response.body.id,
			isActive: response.body.is_active,
			offerImage: response.body.image?.path ?? '',
			postalCode: response.body.postal_code,
			startDate: response.body.start_date,
			title: response.body.title,
			vendorEmail: response.body.vendor.email_address,
			vendorName: response.body.vendor.name,
			vendorPhone: response.body.vendor.phone_number,
			website: response.body.website_url,
		};
	}

	/**
	 * @param {Offer} data
	 * @param {string} offerId
	 */
	async editOffer(data, offerId) {
		const isActive = data.isActive === 'active';

		const response = await this.httpClient.put(
			`${apiUrls.offers.root}/${offerId}`,
			new Headers({
				Authorization: `Bearer ${this.authService.getAccessToken()}`,
				'Content-Type': 'application/json',
			}),
			{
				/* eslint-disable camelcase -- Mapping request body */

				category: data.category,
				description: data.description,
				end_date: data.endDate,
				is_active: isActive,
				postal_code: data.postalCode,
				start_date: data.startDate,
				title: data.title,
				vendor: {
					email_address: data.vendorEmail,
					name: data.vendorName,
					phone_number: data.vendorPhone,
				},
				website_url: data.website,
				/* eslint-enable camelcase */
			}
		);

		if (!response.isOk) {
			throw new ServiceError('Unable to edit offer.', response.body);
		}
	}

	/**
	 * @param {File} image
	 * @param {string} offerId
	 */
	async addOfferImage(image, offerId) {
		const formData = new FormData();
		formData.append('file', image);

		const response = await this.httpClient.put(
			`${apiUrls.offers.root}/${offerId}/image`,
			new Headers({
				Authorization: `Bearer ${this.authService.getAccessToken()}`,
			}),
			formData
		);

		if (!response.isOk) {
			throw new ServiceError('Unable to add offer image.', response.body);
		}
	}

	/**
	 * @param {Offer} data
	 * @returns {Promise<string>}
	 */
	async addAgentOffer(data) {
		const isActive = data.isActive === 'active';

		const response = await this.httpClient.post(
			`${apiUrls.agents.root}/${await this.getAgentId()}/offers`,
			new Headers({
				Authorization: `Bearer ${this.authService.getAccessToken()}`,
				'Content-Type': 'application/json',
			}),
			{
				/* eslint-disable camelcase -- Mapping request body */

				category: data.category,
				description: data.description,
				end_date: data.endDate,
				is_active: isActive,
				postal_code: data.postalCode,
				start_date: data.startDate,
				title: data.title,
				vendor: {
					email_address: data.vendorEmail,
					name: data.vendorName,
					phone_number: data.vendorPhone,
				},
				website_url: data.website,
				/* eslint-enable camelcase */
			}
		);

		if (!response.isOk) {
			throw new ServiceError('Unable to add agent offer.', response.body);
		}

		return response.body.id;
	}

	/**
	 * @param {Offer} data
	 * @param {string} groupId
	 */
	async addGroupOffer(data, groupId) {
		const isActive = data.isActive === 'active';

		const response = await this.httpClient.post(
			`${apiUrls.me.offers.addGroup}/${groupId}/offers`,
			new Headers({
				Authorization: `Bearer ${this.authService.getAccessToken()}`,
				'Content-Type': 'application/json',
			}),
			{
				/* eslint-disable camelcase -- Mapping request body */

				category: data.category,
				description: data.description,
				end_date: data.endDate,
				is_active: isActive,
				postal_code: data.postalCode,
				start_date: data.startDate,
				title: data.title,
				vendor: {
					email_address: data.vendorEmail,
					name: data.vendorName,
					phone_number: data.vendorPhone,
				},
				website_url: data.website,
				/* eslint-enable camelcase */
			}
		);

		if (!response.isOk) {
			throw new ServiceError(
				translate('admin.pages.offers.error_message'),
				response.body
			);
		}
	}

	/**
	 * @param {boolean} value
	 * @param {'administrator' | 'agent'} type
	 * @returns {Promise<void>}
	 */
	async updateNetworkVendors(value, type) {
		const response = await this.httpClient.put(
			`${apiUrls.me.root}/${type}/network-vendors`,
			new Headers({
				Authorization: `Bearer ${this.authService.getAccessToken()}`,
				'Content-Type': 'application/json',
			}),
			{
				// eslint-disable-next-line camelcase
				suppress_external_vendors: value,
			}
		);

		if (!response.isOk) {
			throw new ServiceError(
				translate('api_error.update'),
				response.body
			);
		}
	}
}
